/* -------------------------------------------------------------------------- */
/*                                  Packages                                  */
/* -------------------------------------------------------------------------- */
import * as React from "react"
import { Logo, NavHeadshot, NewsroomNav, SearchNav } from "@abmg-components"
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image"
import { graphql, Link, useStaticQuery } from "gatsby"
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Search,
  X,
} from "react-feather"
import Headroom from "react-headroom"
import { AnimatePresence, motion } from "framer-motion"
import { twMerge } from "tw-merge"
import { isMobile } from "react-device-detect"

/* -------------------------------------------------------------------------- */
/*                             External Components                            */
/* -------------------------------------------------------------------------- */
import ClientSideOnly from "@components/global/client-side-only"

/* -------------------------------------------------------------------------- */
/*                                    Data                                    */
/* -------------------------------------------------------------------------- */
import siteData from "@data/site.json"

/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */
import useClientSideOnly from "@hooks/use-client-side-only"

/* -------------------------------------------------------------------------- */
/*                                   Images                                   */
/* -------------------------------------------------------------------------- */
import Home from "@images/knowledge-center/home.svg"

/* -------------------------------------------------------------------------- */
/*                                    Utils                                   */
/* -------------------------------------------------------------------------- */
import { CategoryBadgeHoverColor } from "@utils/knowledge-center"
import { getUrlWithParams, theme } from "@utils/utils"
import {
  conventionalLoansLink,
  downPaymentAssistanceLink,
  fhaLoansLink,
  fthLink,
  homeAffordabilityLink,
  homeEquityLink,
  homeLoansLink,
  homeownerHubLink,
  jumboLoansLink,
  mortgageApprovalLink,
  mortgageProcessLink,
  mortgageRatesLink,
  refinanceLink,
  selfEmployedLoansLink,
  usdaLoansLink,
  vaLoansLink,
} from "@utils/pillar-links"

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */
const chevronClasses = `[--size:20px] flex-shrink-0 h-[var(--size)] w-[var(--size)] [transition:all_500ms_ease]`

/* -------------------------------------------------------------------------- */
/*                                 Components                                 */
/* -------------------------------------------------------------------------- */

const KcNavItem = ({ item }) => {
  const isCurrentKCNavLink = ({ href, location }) => {
    let linkClass
    const basePath = location.pathname
      .split("/")
      .filter(string => string !== "")
    const pathEnd = basePath[basePath.length - 1]
    if (href.match("financial")) linkClass = "financial"
    else if (href.match("house")) linkClass = "home"
    else if (href.match("mortgage")) linkClass = "mortgage"
    else if (pathEnd === "knowledge-center") linkClass = "KC"
    return href === location.pathname + location.search
      ? { className: `active ${linkClass}` }
      : {}
  }
  const sharedNavProps = {
    activeClassName: "active",
    partiallyActive: true,
    getProps: isCurrentKCNavLink,
  }
  return (
    <li className="list_item--reset" key={item.text}>
      <Link className={`${item.className}`} to={item.to} {...sharedNavProps}>
        {item.icon && <img src={item.icon} />} <span>{item.text}</span>
      </Link>
    </li>
  )
}
const KcNav = props => {
  const baseSlug = `knowledge-center`
  const navItems = [
    {
      className: `KC`,
      icon: Home,
      text: `KC`,
      to: `/${baseSlug}/`,
    },
    {
      className: "mortgage",
      text: ` Mortgage Matters`,
      to: `/${baseSlug}/mortgage-matters/`,
    },
    {
      className: "financial",
      text: `Financial Wellness`,
      to: `/${baseSlug}/financial-wellness/`,
    },
    {
      className: "home",
      text: `House to Home`,
      to: `/${baseSlug}/house-to-home/`,
    },
  ]

  const mobileNavItems = navItems.map(item => {
    if (item.text.match(/mortgage/i)) {
      return {
        ...item,
        text: "Mortgage",
      }
    }
    if (item.text.match(/financial/i)) {
      return {
        ...item,
        text: "Financial",
      }
    }
    if (item.text.match(/house/i)) {
      return {
        ...item,
        text: "At Home",
      }
    }
    return item
  })
  const suggestedSearchButtons = [
    "First-time home buyer",
    "Affordable",
    "Military",
    "Credit",
    "Refinance",
  ]

  return (
    <div
      className={`[transition:transform_200ms_ease-in-out] relative ${
        !props.navIsPinned && props.articleSlug
          ? "translate-y-[99%]"
          : "translate-y-0"
      }`}
    >
      <SearchNav
        navItems={navItems.map(item => (
          <KcNavItem key={item.text} item={item} />
        ))}
        mobileNavItems={mobileNavItems.map(item => (
          <KcNavItem key={item.text} item={item} />
        ))}
        articleSlug={props.articleSlug}
        suggestedSearchButtons={suggestedSearchButtons}
        categoryBadgeHoverColor={CategoryBadgeHoverColor(props.articleCategory)}
        articleCategory={props.articleCategory}
        articleProgressBarRoot={props.articleProgressBarRoot}
        articleProgressBarContainer={props.articleProgressBarContainer}
        navIsOpen={props.navIsOpen}
        searchBoxFocus={props.searchBoxFocus}
        setSearchBoxFocus={props.setSearchBoxFocus}
      />
    </div>
  )
}

type NewsNavProps = {
  navIsPinned: boolean
  pathname: string
  series: string | null
}

const NewsNav = (props: NewsNavProps) => {
  const navItems = [
    {
      className: props.pathname === "/newsroom/" ? "active" : "",
      text: `Newsroom`,
      to: `/newsroom/`,
    },
    {
      className: props.series ? "active" : "",
      text: `Stories`,
      to: `/newsroom/stories/`,
    },
    {
      className: props.pathname === "/newsroom/facts/" ? "active" : "",
      text: (
        <>
          {" "}
          Facts<span> and Figures</span>
        </>
      ),
      to: `/newsroom/facts/`,
    },
    {
      className: props.pathname === "/newsroom/awards/" ? "active" : "",
      text: `Awards`,
      to: `/newsroom/awards/`,
    },
    {
      className: props.pathname === "/newsroom/press/" ? "active" : "",
      text: `Press`,
      to: `/newsroom/press/`,
    },
  ]
  return (
    // <div
    //   className={`[transition:transform_200ms_ease-in-out] relative ${
    //     !props.navIsPinned ? "translate-y-[99%]" : "translate-y-0"
    //   }`}
    // >
    <NewsroomNav
      navItems={navItems.map(item => {
        return (
          <li
            className="list_item--reset !leading-[64px]"
            key={`newsroom-nav-item-${item.text}`}
          >
            <Link to={item.to} className={item.className}>
              {item.text}
            </Link>
          </li>
        )
      })}
    />
    // </div>
  )
}

const FullDropdown = props => {
  if (!props.link) return null
  const componentRef = React.useRef(null)
  const bodyRef = React.useRef(null)
  const typedDropdownLink = props.link as DropdownLinkProps
  const dropdown = typedDropdownLink.dropdown
  const isCurrentFullDropdown =
    props.fullDropdown && props.fullDropdown.heading === dropdown.heading
  const CtaBannerLinkTag = dropdown?.ctaBanner?.cta?.to ? Link : "a"
  const FeaturedProgramLinkTag = dropdown?.featuredProgram?.to ? Link : "a"

  return (
    <motion.div
      className={`[--border-color:#E2E8F0] absolute grid grid-rows-[auto_1fr] items-stretch left-0 bg-grey-1 [border-radius:0_0_10px_10px] border-b border-b-[var(--border-color)] border-t border-t-[var(--border-color)] min-h-[542px] [padding:28px_38px] w-full top-[calc(100%)] transition-all  ${
        isCurrentFullDropdown ? "z-[10]" : ""
      }`}
      initial={{
        display: isCurrentFullDropdown ? "grid" : "none",
        opacity: isCurrentFullDropdown ? 1 : 0,
      }}
      animate={{
        display: isCurrentFullDropdown ? "grid" : "none",
        opacity: isCurrentFullDropdown ? 1 : 0,
      }}
      ref={
        props.index === undefined
          ? componentRef
          : el => (props.fullDropdownsRef.current[props.index] = el)
      }
      key={`fullDropdown-heading-${dropdown?.heading}`}
      onKeyDown={e => {
        if (e.key === "Escape") {
          props.setFullDropdown(null)
        }
      }}
      onMouseMove={e => {
        const shouldShowSideDropdown = bodyRef.current.contains(e.target)

        if (!shouldShowSideDropdown) {
          if (props.sideDropdown) props.setSideDropdown(null)
        }
      }}
    >
      <header className="flex flex-col gap-[8px] [margin-block-end:20px]">
        <p className="font-bold leading-[24px] [margin-block-end:0] text-[20px] text-grey-7">
          {dropdown.heading}
        </p>
        <div className="[&_*]:leading-[20px] [&_p:last-child]:[margin-block-end:0] [&_*]:text-[14px] [&_*]:text-grey-6">
          {dropdown.description}
        </div>
      </header>

      <div
        className="gap-[86px] grid grid-cols-[1fr_minmax(400px,424px)]"
        ref={bodyRef}
      >
        {/* Left Side */}
        <div className="flex flex-col">
          <hr className="border-b-[#E2E8F0] " />
          <ul className="grid gap-x-[7px] gap-y-[20px] grid-cols-2 [margin-block:16px_42px]">
            {dropdown.links?.map(dropdownLink => {
              const typedParentSublink = dropdownLink as ParentSublinkProps
              const typedInternalSublink = dropdownLink as InternalSublinkProps
              const typedExternalSublink = dropdownLink as ExternalSublinkProps
              const typedDropdownLink = dropdownLink as SharedSublinkProps

              const ItemTag =
                typedParentSublink.links &&
                !typedInternalSublink.to &&
                !typedExternalSublink.href
                  ? "button"
                  : typedInternalSublink.to
                    ? Link
                    : "a"

              const shouldShowLinks =
                props.sideDropdown &&
                props.sideDropdown.heading === typedParentSublink.heading

              return (
                <li
                  key={`full-dropdown-dropdown-link-${dropdownLink.heading}`}
                  className="list_item--reset flex-col transition-all [&_*:not(svg)]:transition-all"
                >
                  <ItemTag
                    className={`group/link flex justify-between items-center [padding:13px_32px] w-full hover:bg-[#EBF8FF]  ${
                      shouldShowLinks ? "bg-[#EBF8FF]" : ""
                    }`}
                    href={typedExternalSublink.href}
                    to={typedInternalSublink.to}
                    target={typedExternalSublink.href ? "_blank" : undefined}
                    onKeyDown={e => {
                      if (e.key === "Escape") {
                        props.setSideDropdown(null)
                      }
                    }}
                    onMouseMove={() => {
                      if (
                        typedParentSublink.links &&
                        props.sideDropdown?.heading !==
                          typedParentSublink.heading
                      )
                        props.setSideDropdown(typedParentSublink)
                    }}
                    onFocus={() => {
                      if (typedParentSublink.links)
                        props.setSideDropdown(typedParentSublink)
                    }}
                  >
                    <div className="flex flex-col items-start">
                      <p
                        className={`font-variant-numeric--secondary capitalize font-bold leading-[20px] [margin-block-end:0] text-[15px] tracking-[.6px] group-hover/link:text-pacific-blue ${
                          shouldShowLinks ? "text-pacific-blue" : ""
                        }`}
                      >
                        {typedDropdownLink.heading}
                      </p>
                      <div
                        className={`[&_*]:font-variant-numeric--secondary [&_*]:leading-[20px] [&_*]:text-[13px] [&_*]:text-grey-6 [&_*]:text-left [&_*]:tracking-[.52px] [&_p:last-child]:[margin-block-end:0] group-hover/link:[&_*]:text-grey-8 ${
                          shouldShowLinks ? "[&_*]:text-grey-8" : ""
                        }`}
                      >
                        {typedDropdownLink.description}
                      </div>
                    </div>
                    {typedParentSublink.links && (
                      <ChevronRight className={`${chevronClasses}`} />
                    )}
                  </ItemTag>
                </li>
              )
            })}
          </ul>
          {/* CTA Banner */}
          {dropdown?.ctaBanner && (
            <div className="bg-[#D7F2FD] flex gap-[12px] items-center justify-between [margin-block-start:auto] max-w-[711px] p-[16px] rounded-[8px] self-start w-full">
              <div className="flex gap-[12px] items-center">
                <div className="[--size:36px] bg-white flex items-center justify-center h-[var(--size)] p-[6px] rounded-full w-[var(--size)]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M19.4272 11.2665C19.4272 15.3423 16.4683 19.1597 12.4258 20.2766C12.1507 20.3516 11.8507 20.3516 11.5756 20.2766C7.53314 19.1597 4.57422 15.3423 4.57422 11.2665V7.60738C4.57422 6.92391 5.091 6.14876 5.7328 5.89037L10.3754 3.99002C11.4172 3.56493 12.5925 3.56493 13.6344 3.99002L18.277 5.89037C18.9104 6.14876 19.4355 6.92391 19.4355 7.60738L19.4272 11.2665Z"
                      stroke="#0274E8"
                      stroke-width="1.2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M14.8083 9.24437L11.3183 12.7444L9.44826 10.7944C9.37321 10.7123 9.2825 10.6461 9.18148 10.5997C9.08045 10.5532 8.97114 10.5274 8.86001 10.5239C8.74887 10.5203 8.63814 10.539 8.53435 10.5789C8.43055 10.6188 8.33579 10.6791 8.25565 10.7562C8.17551 10.8333 8.1116 10.9256 8.0677 11.0278C8.0238 11.1299 8.00079 11.2398 8.00002 11.351C7.99925 11.4622 8.02075 11.5725 8.06324 11.6752C8.10573 11.778 8.16835 11.8712 8.24743 11.9494L10.1691 13.9494C10.3124 14.1042 10.4856 14.2283 10.6783 14.3142C10.8709 14.4002 11.079 14.4461 11.2899 14.4494H11.3174C11.5241 14.4501 11.7289 14.4097 11.9199 14.3306C12.1109 14.2515 12.2842 14.1352 12.4299 13.9885L15.9899 10.4285C16.0677 10.3509 16.1294 10.2588 16.1715 10.1574C16.2137 10.0559 16.2354 9.94718 16.2355 9.83733C16.2357 9.72749 16.2141 9.6187 16.1722 9.51717C16.1303 9.41564 16.0688 9.32337 15.9912 9.24562C15.9136 9.16786 15.8214 9.10615 15.72 9.06401C15.6186 9.02187 15.5098 9.00012 15.4 9C15.2901 8.99988 15.1813 9.02141 15.0798 9.06333C14.9783 9.10526 14.886 9.16678 14.8083 9.24437Z"
                      fill="#0274E8"
                    />
                  </svg>
                </div>

                <div className="[&_*]:font-semibold [&_*]:leading-[normal] [&_*]:text-[14px] [&_*]:text-grey-7 [&_p:last-child]:[margin-block-end:0]">
                  {dropdown?.ctaBanner?.description}
                </div>
              </div>
              <CtaBannerLinkTag
                className="button button--primary"
                href={dropdown?.ctaBanner?.cta?.href}
                to={dropdown?.ctaBanner?.cta?.to}
                target={dropdown?.ctaBanner?.cta?.href ? "_blank" : undefined}
              >
                {dropdown?.ctaBanner?.cta?.text}
              </CtaBannerLinkTag>
            </div>
          )}
        </div>
        {/* Right Side */}
        <div className={`flex flex-col gap-[19px] max-w-[424px]`}>
          {props.link.dropdown.links.map(link => {
            if (link.links) {
              const shouldShowLinks =
                props.sideDropdown &&
                props.sideDropdown.heading === link.heading
              return (
                <motion.ul
                  // className={`${shouldShowLinks ? "" : "hidden"}`}
                  key={`sidedropdown-${link.heading}`}
                  animate={{
                    display: shouldShowLinks ? "block" : "none",
                    opacity: shouldShowLinks ? 1 : 0,
                  }}
                >
                  {link.links.map(sublink => {
                    const typedParentSublink = sublink as ParentSublinkProps
                    const typedInternalSublink = sublink as InternalSublinkProps
                    const typedExternalSublink = sublink as ExternalSublinkProps
                    const typedDropdownLink = sublink as SharedSublinkProps

                    const ItemTag = typedInternalSublink.to ? Link : "a"

                    const LinkTag = sublink.to ? Link : "a"
                    return (
                      <li
                        key={`sidedropdown-sublink-${sublink.heading}`}
                        className="list_item--reset [padding:18px_8px] text-grey-7 transition-all [&_>_*]:w-full  [&_a:active]:no-underline"
                      >
                        <LinkTag
                          to={sublink.to}
                          href={sublink.href}
                          target={sublink.href ? "_blank" : undefined}
                          className="group/link flex gap-[12px] items-center"
                        >
                          <div className="[--size:8px] bg-[#E2E8F0] h-[var(--size)] rounded-full w-[var(--size)] group-hover/link:bg-pacific-blue group-active/link:bg-pacific-blue group-focus/link:bg-pacific-blue"></div>

                          <div className="flex flex-col items-start">
                            <p className="font-variant-numeric--secondary capitalize font-bold leading-[20px] [margin-block-end:0] text-[15px] text-grey-7 tracking-[.6px] group-hover/link:text-pacific-blue">
                              {sublink.heading}
                            </p>
                            <div className="[&_*]:font-variant-numeric--secondary [&_*]:leading-[20px] [&_*]:text-[13px] [&_*]:text-grey-6 [&_*]:tracking-[.52px] [&_p:last-child]:[margin-block-end:0] ">
                              {sublink.description}
                            </div>
                          </div>
                        </LinkTag>
                      </li>
                    )
                  })}
                </motion.ul>
              )
            }
          })}
          {/* <ul className={`${props.sideDropdown ? "" : "hidden"}`}>
            {props.sideDropdown?.links?.map(link => {
              const LinkTag = link.to ? Link : "a"
              return (
                <li
                  key={`sidedropdown-link-${link.heading}`}
                  className="list_item--reset [padding:18px_8px] text-grey-7 transition-all [&_*]:text-[15px] [&_*]:[color:inherit] [&_>_*]:w-full  [&_a:active]:no-underline"
                >
                  <LinkTag
                    to={link.to}
                    href={link.href}
                    target={link.href ? "_blank" : undefined}
                    className="group/link flex gap-[12px] items-center"
                  >
                    <div className="[--size:8px] bg-[#E2E8F0] h-[var(--size)] rounded-full w-[var(--size)] group-hover/link:bg-pacific-blue group-active/link:bg-pacific-blue group-focus/link:bg-pacific-blue"></div>

                    <div className="flex flex-col items-start">
                      <p className="font-variant-numeric--secondary capitalize font-bold leading-[20px] [margin-block-end:0] text-[15px] tracking-[.6px] group-hover/link:text-pacific-blue">
                        {link.heading}
                      </p>
                      <div className="[&_*]:font-variant-numeric--secondary [&_*]:leading-[20px] [&_*]:text-[13px] [&_*]:text-grey-6 [&_*]:tracking-[.52px] [&_p:last-child]:[margin-block-end:0] ">
                        {link.description}
                      </div>
                    </div>
                  </LinkTag>
                </li>
              )
            })}
          </ul> */}

          <motion.div
            className={`flex flex-col gap-[19px] ${
              // props.sideDropdown ? "hidden" : ""
              ""
            }`}
            animate={{
              display: !props.sideDropdown ? "flex" : "none",
              opacity: !props.sideDropdown ? 1 : 0,
            }}
          >
            <FeaturedProgramLinkTag
              className={`flex flex-col gap-[19px] ${
                // props.sideDropdown ? "hidden" : ""
                ""
              }`}
              to={dropdown?.featuredProgram?.to}
              href={dropdown?.featuredProgram?.href}
              target={dropdown?.featuredProgram?.href ? "_blank" : undefined}
              onBlur={() => {
                if (!dropdown.featuredProgram.popularPaths)
                  props.setSideDropdown(null)
              }}
            >
              <GatsbyImage
                alt={dropdown?.featuredProgram?.altText}
                className={`h-[138px] rounded-[8px] `}
                image={dropdown?.featuredProgram?.image}
                imgStyle={{
                  objectPosition: dropdown?.featuredProgram?.objectPosition,
                }}
              />

              {/* Program Details Container */}
              <div className="flex flex-col gap-[8px]">
                <p className="font-bold leading-[normal] text-[12px] text-pacific-blue tracking-[.48px] [margin-block-end:0] uppercase">
                  Featured Program
                </p>
                <p className="capitalize font-bold leading-[normal] text-[18px] text-grey-7 [margin-block-end:0]">
                  {dropdown?.featuredProgram?.heading}
                </p>
                <div className="[&_*]:leading-[20px] [&_*]:text-[14px] [&_*]:text-grey-6 [&_*]:[margin-block-end:0]">
                  {dropdown?.featuredProgram?.description}
                </div>
              </div>
            </FeaturedProgramLinkTag>
            <hr className="border-b-[#E2E8F0]" />
            {dropdown?.featuredProgram?.popularPaths && (
              <div className="[--gap:10px] flex flex-col gap-[var(--gap)]">
                <p className="font-bold leading-[normal] text-[13px] text-grey-5 [margin-block-end:0]">
                  Other popular paths:
                </p>
                <ul className="flex flex-col gap-[var(--gap)]">
                  {dropdown.featuredProgram.popularPaths.map(
                    (popularPath, i) => {
                      const LinkTag = popularPath.to ? Link : "a"
                      return (
                        <li
                          className="list_item--reset !leading-[normal]"
                          key={`popular-path-${popularPath.text}`}
                        >
                          <LinkTag
                            className="font-semibold leading-[normal] text-[14px] text-pacific-blue transition-all hover:!text-arctic-blue"
                            href={popularPath.href}
                            to={popularPath.to}
                            target={popularPath.href ? "_blank" : undefined}
                            onBlur={() => {
                              if (
                                i ===
                                dropdown.featuredProgram.popularPaths.length - 1
                              )
                                props.setSideDropdown(null)
                            }}
                          >
                            → {popularPath.text}
                          </LinkTag>
                        </li>
                      )
                    },
                  )}
                </ul>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </motion.div>
  )
}

const Socials = ({ socials }) => {
  return (
    <div className="flex gap-[11px] justify-center [&_:hover_svg_path:first-child]:fill-pacific-blue">
      {socials.map(social => {
        if (!social) return null
        return (
          <a href={social.href} target="_blank" key={`${social.type}`}>
            {social.type === "facebook" ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M19.9932 10.0005C19.9932 15.051 16.2505 19.2264 11.3884 19.904C10.9338 19.9671 10.4687 20 9.99659 20C9.45161 20 8.91644 19.9566 8.39528 19.8725C3.6355 19.1059 0 14.9775 0 10.0005C0 4.47764 4.47608 0.000244141 9.99729 0.000244141C15.5185 0.000244141 19.9946 4.47764 19.9946 10.0005H19.9932Z"
                  fill="#0B395B"
                />
                <path
                  d="M11.3884 8.03006V10.2085H14.0825L13.6559 13.143H11.3884V19.9039C10.9338 19.967 10.4687 19.9999 9.99656 19.9999C9.45158 19.9999 8.91642 19.9565 8.39526 19.8724V13.143H5.91064V10.2085H8.39526V7.54309C8.39526 5.88946 9.73528 4.54834 11.3891 4.54834V4.54974C11.394 4.54974 11.3982 4.54834 11.4031 4.54834H14.0832V7.08624H12.332C11.8115 7.08624 11.3891 7.50875 11.3891 8.02937L11.3884 8.03006Z"
                  fill="white"
                />
              </svg>
            ) : social.type === "instagram" ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M10.0197 0H9.97417C4.46559 0 0 4.4669 0 9.97711V10.0226C0 15.5329 4.46559 19.9998 9.97417 19.9998H10.0197C15.5283 19.9998 19.9939 15.5329 19.9939 10.0226V9.97711C19.9939 4.4669 15.5283 0 10.0197 0Z"
                  fill="#0B395B"
                />
                <path
                  d="M13.091 4.06885H6.90432C5.19515 4.06885 3.80469 5.45971 3.80469 7.1694V12.831C3.80469 14.5406 5.19515 15.9315 6.90432 15.9315H13.091C14.8002 15.9315 16.1906 14.5406 16.1906 12.831V7.1694C16.1906 5.45971 14.8002 4.06885 13.091 4.06885ZM4.89814 7.1694C4.89814 6.06301 5.79826 5.16262 6.90432 5.16262H13.091C14.197 5.16262 15.0972 6.06301 15.0972 7.1694V12.831C15.0972 13.9373 14.197 14.8377 13.091 14.8377H6.90432C5.79826 14.8377 4.89814 13.9373 4.89814 12.831V7.1694Z"
                  fill="white"
                />
                <path
                  d="M9.99793 12.8833C11.5873 12.8833 12.8811 11.5898 12.8811 9.99927C12.8811 8.4087 11.588 7.11523 9.99793 7.11523C8.40784 7.11523 7.11475 8.4087 7.11475 9.99927C7.11475 11.5898 8.40784 12.8833 9.99793 12.8833ZM9.99793 8.20971C10.9849 8.20971 11.7877 9.0127 11.7877 9.99997C11.7877 10.9872 10.9849 11.7902 9.99793 11.7902C9.01095 11.7902 8.2082 10.9872 8.2082 9.99997C8.2082 9.0127 9.01095 8.20971 9.99793 8.20971Z"
                  fill="white"
                />
                <path
                  d="M13.1484 7.58123C13.5764 7.58123 13.9253 7.23299 13.9253 6.80417C13.9253 6.37534 13.5771 6.0271 13.1484 6.0271C12.7197 6.0271 12.3716 6.37534 12.3716 6.80417C12.3716 7.23299 12.7197 7.58123 13.1484 7.58123Z"
                  fill="white"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M10.0197 0.000244141H9.97418C4.4656 0.000244141 0 4.46715 0 9.97735V10.0229C0 15.5331 4.4656 20 9.97418 20H10.0197C15.5283 20 19.9939 15.5331 19.9939 10.0229V9.97735C19.9939 4.46715 15.5283 0.000244141 10.0197 0.000244141Z"
                  fill="#0B395B"
                />
                <path
                  d="M4.76741 6.6481C4.50403 6.40356 4.37305 6.10087 4.37305 5.74072C4.37305 5.38056 4.50473 5.06455 4.76741 4.81931C5.03079 4.57477 5.36983 4.45215 5.78521 4.45215C6.2006 4.45215 6.52633 4.57477 6.78901 4.81931C7.05239 5.06385 7.18337 5.37145 7.18337 5.74072C7.18337 6.10998 7.05169 6.40356 6.78901 6.6481C6.52563 6.89265 6.19149 7.01526 5.78521 7.01526C5.37893 7.01526 5.03079 6.89265 4.76741 6.6481ZM6.96203 8.05089V15.5483H4.59369V8.05089H6.96203Z"
                  fill="#FEFFFC"
                />
                <path
                  d="M14.8469 8.79102C15.3632 9.35157 15.621 10.1209 15.621 11.1005V15.4153H13.3717V11.4046C13.3717 10.9106 13.2436 10.5266 12.9879 10.2534C12.7322 9.98009 12.3876 9.84276 11.9561 9.84276C11.5246 9.84276 11.1799 9.97939 10.9242 10.2534C10.6686 10.5266 10.5404 10.9106 10.5404 11.4046V15.4153H8.27783V8.02937H10.5404V9.00893C10.7694 8.68241 11.0784 8.42457 11.4664 8.23468C11.8545 8.04479 12.2909 7.9502 12.7763 7.9502C13.6407 7.9502 14.3314 8.23047 14.8469 8.79032V8.79102Z"
                  fill="#FEFFFC"
                />
              </svg>
            )}
          </a>
        )
      })}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                                    Props                                   */
/* -------------------------------------------------------------------------- */
interface SharedLinkProps {
  text: string
}

interface ButtonProps extends SharedLinkProps {
  buttonFunctions: {
    onClick?: React.MouseEventHandler<HTMLButtonElement>
  }
}

export interface ExternalLinkProps extends SharedLinkProps {
  href: string
}

export interface InternalLinkProps extends SharedLinkProps {
  to: string
}

type SocialProps = {
  href: string
  type: "facebook" | "instagram" | "linkedin"
}

interface SharedSublinkProps {
  description: React.ReactNode
  heading: string
}

interface ExternalSublinkProps extends SharedSublinkProps {
  href: string
}

interface InternalSublinkProps extends SharedSublinkProps {
  to: string
}

interface ParentSublinkProps extends SharedSublinkProps {
  links: Array<ExternalSublinkProps | InternalSublinkProps>
}

interface DropdownLinkProps extends SharedLinkProps {
  dropdown: DropdownProps
}

type DropdownProps = {
  ctaBanner: {
    cta: InternalLinkProps | ExternalLinkProps
    description: React.ReactNode
  }
  description: React.ReactNode
  featuredProgram: {
    altText: string
    description: React.ReactNode
    heading: string
    image: IGatsbyImageData
    popularPaths: Array<InternalLinkProps>
    to: string
  }
  heading: string
  links: Array<InternalSublinkProps | ExternalSublinkProps | ParentSublinkProps>
}

interface SharedParentLinkProps {
  text: string
  links: Array<InternalLinkProps | ExternalLinkProps>
}
interface ExternalParentLinkProps extends SharedParentLinkProps {
  href: string
}
interface InternalParentLinkProps extends SharedParentLinkProps {
  to: string
}

type BasicDropdownLinkProps = {
  dropdown: BasicDropdownProps
}

type BasicDropdownProps = {
  links: Array<
    | InternalLinkProps
    | ExternalLinkProps
    | InternalParentLinkProps
    | ExternalParentLinkProps
  >
}
interface MobileDropdownLinkProps extends SharedLinkProps {
  dropdown: MobileDropdownProps
}
type MobileDropdownProps = {
  ctaLinks?: Array<InternalLinkProps | ExternalLinkProps | ButtonProps>
  description: React.ReactNode
  heading: string
  links: Array<InternalSublinkProps | ExternalSublinkProps | ParentSublinkProps>
}
type Props = {
  bankerData?: object
  base: {
    cta?: InternalLinkProps | ExternalLinkProps | ButtonProps
    links?: Array<InternalLinkProps | ExternalLinkProps | DropdownLinkProps>
  }
  logo: {
    logo?: React.ReactNode
    to?: string
  }
  mobile: {
    ctaLinks?: Array<InternalLinkProps | ExternalLinkProps>
    links: Array<
      MobileDropdownLinkProps | InternalLinkProps | ExternalLinkProps
    >
    socials?: Array<SocialProps>
  }
  navIsOpen: boolean
  upper: {
    links?: Array<
      InternalLinkProps | ExternalLinkProps | BasicDropdownLinkProps
    >
    socials?: Array<SocialProps>
  }
  variants: {
    disableNavPinning?: boolean
    useKcNav?: boolean
    useNewsroomNav?: boolean
    useSearch?: boolean
    type: "corporate" | "mb" | "other" | "mb-other"
  }
}

/* -------------------------------------------------------------------------- */
/*                              Default Component                             */
/* -------------------------------------------------------------------------- */
export default function Nav(props: Props) {
  const isMounted = useClientSideOnly()
  const data = useStaticQuery(graphql`
    query {
      FthFeaturedProgramImage: file(
        relativePath: { regex: "/pillars/fth/header/" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 424)
        }
      }
      HomeEquityFeaturedProgramImage: file(
        relativePath: { regex: "/pillars/home-equity/header/" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 424)
        }
      }
      RatesFeaturedProgramImage: file(
        relativePath: { regex: "/pillars/mortgage-rates/header/" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 424)
        }
      }
      ResourcesFeaturedProgramImage: file(
        relativePath: {
          regex: "/pillars/home-loans/conventional-loans/header/"
        }
      ) {
        childImageSharp {
          gatsbyImageData(width: 424)
        }
      }
      RefinanceFeaturedProgramImage: file(
        relativePath: { regex: "/pillars/refinance/header/" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 424)
        }
      }
    }
  `)
  let socials = [
    {
      href: siteData.socials.facebook,
      type: "facebook",
    },
    {
      href: siteData.socials.instagram,
      type: "instagram",
    },
    {
      href: siteData.socials.linkedin,
      type: "linkedin",
    },
  ]

  let corporateLinks = {
    about: {
      links: [
        {
          dropdown: {
            heading: "About",
            links: [
              {
                description: <p>Our story, people, and values.</p>,
                heading: `About Atlantic Bay`,
                text: `About Atlantic Bay`,
                to: `/about/`,
              },
              {
                description: <p>How our team gives back.</p>,
                heading: `AB Cares Foundation`,
                text: `AB Cares Foundation`,
                to: `/abcares/`,
              },
              {
                description: <p>Explore roles across Atlantic Bay.</p>,
                heading: `Careers`,
                text: `Careers`,
                to: `/careers/`,
                links: [
                  {
                    description: <p>See why top loan officers join us.</p>,
                    heading: `Sales Careers`,
                    href: `https://www.joinatlanticbay.com`,
                    text: `Sales Careers`,
                  },
                ],
              },
              {
                description: <p>Get in touch with our team.</p>,
                heading: `Contact`,
                text: `Contact`,
                to: `/contact/`,
              },
              {
                description: <p>Company news, awards, and updates.</p>,
                heading: `Newsroom`,
                text: `Newsroom`,
                to: `/newsroom/`,
              },
            ],
          },
          text: "About",
        },
      ],
    },
    buyAHome: {
      description: <p>Explore paths, tools, and loan options in one place.</p>,
      heading: `Buy a Home`,
      links: [
        {
          description: <p>Learn what you can realistically afford.</p>,
          heading: `Mortgage pre-approval`,
          to: mortgageApprovalLink,
        },
        {
          description: <p>Resources, guides, and mortgage education.</p>,
          heading: `First-Time Homebuyer`,
          to: fthLink,
          links: [
            {
              description: <p>Programs that help lower your upfront cost.</p>,
              heading: `Down Payment Assistance`,
              to: downPaymentAssistanceLink,
            },
          ],
        },
        {
          description: <p>Find the loan that fits your needs.</p>,
          heading: `Home Loan Options`,
          links: [
            {
              description: <p>A solid option for most homebuyers.</p>,
              heading: `Conventional Loans`,
              to: conventionalLoansLink,
            },
            {
              description: <p>Lower down payments for qualified buyers.</p>,
              heading: `FHA Loans`,
              to: fhaLoansLink,
            },
            {
              description: <p>Zero down financing for those who served.</p>,
              heading: `VA Loans`,
              to: vaLoansLink,
            },
            {
              description: <p>Zero down loans for eligible rural homes.</p>,
              heading: `USDA Loans`,
              to: usdaLoansLink,
            },
            {
              description: <p>Loans built for higher-priced homes.</p>,
              heading: `Jumbo Loans`,
              to: jumboLoansLink,
            },
            {
              description: <p>Financing with non-traditional income.</p>,
              heading: `Self-Employed Loans`,
              to: selfEmployedLoansLink,
            },
          ],
          to: homeLoansLink,
        },
        {
          description: <p>A simple breakdown of every step.</p>,
          heading: `The Mortgage Process`,
          to: mortgageProcessLink,
        },
        {
          description: <p>See what fits comfortably in your budget.</p>,
          heading: `Home Affordability`,
          to: homeAffordabilityLink,
        },
        // {
        //   description: <p>Get the details on second home loans.</p>,
        //   heading: `Buy a Second Home`,
        //   to: secondHomeLink,
        // },
      ],
    },
    refinance: {
      description: <p>Rethink your mortgage on your terms.</p>,
      heading: `Refinance`,
      links: [
        {
          description: <p>Update your rate to match today's market.</p>,
          heading: `Refinance Your Mortgage Rate`,
          to: refinanceLink,
        },
        // {
        //   description: <p>Find out if you qualify for less.</p>,
        //   heading: `Get a Lower Rate`,
        //   to: rateTermRefinanceLink,
        // },
        // {
        //   description: <p>Turn your home equity into cash.</p>,
        //   heading: `Take Cash Out`,
        //   to: cashOutRefinanceLink,
        // },
        // {
        //   description: <p>A simple breakdown of the steps.</p>,
        //   heading: `The Refinance Process`,
        //   to: refinanceProcess,
        // },
      ],
    },
    homeEquity: {
      description: <p>Fully tap into your home’s value.</p>,
      heading: `Home Equity`,
      links: [
        {
          description: <p>Borrow against the value you've built.</p>,
          heading: `Home Equity Loans`,
          to: homeEquityLink,
        },
        // {
        //   description: <p>Replace your loan and get cash back.</p>,
        //   heading: `Cash-Out Refinance`,
        //   to: cashOutRefinanceLink,
        // },
        // {
        //   description: <p>Use your equity to pay down debt.</p>,
        //   heading: `Debt Consolidation`,
        //   to: debtConsolidation,
        // },
      ],
    },
    rates: {
      description: <p>Keep an eye on what’s changing.</p>,
      heading: `Rates`,
      links: [
        // {
        //   description: <p>Lower payments early with future shifts.</p>,
        //   heading: `Adjustable Rates`,
        //   to: adjustableRates,
        // },
        {
          description: (
            <p>
              A guide to how mortgage rates work for both purchases and
              refinances.
            </p>
          ),
          heading: `Mortgage Rates`,
          to: mortgageRatesLink,
        },
        {
          description: <p>Plug in your numbers, see your options.</p>,
          heading: `Calculators`,
          links: [
            {
              description: <p>Find out how much home you can afford.</p>,
              heading: `Affordability Calculator`,
              to: `/affordability-calculator/`,
            },
          ],
        },

        // {
        //   description: <p>Your rate never changes, ever.</p>,
        //   heading: `Fixed Rates`,
        //   links: [
        // {
        //   description: <p>Your rate never changes, ever.</p>,
        //   heading: `Fixed Rates`,
        //   to: fixedRateLink,
        // },
        // {
        //   description: <p>Lower monthly payments over 30 years.</p>,
        //   heading: `30-Year Fixed Rates`,
        //   to: thirtyYearFixedRateLink,
        // },
        // {
        //   description: <p>Pay off your home in half the time.</p>,
        //   heading: `15-Year Fixed Rates`,
        //   to: fifteenYearFixedRateLink,
        // },
        // ],
        // },
      ],
    },
    resources: {
      description: <p>A few extra tools every homebuyer could use.</p>,
      heading: `Resources`,
      links: [
        {
          description: <p>Support to cover upfront costs.</p>,
          heading: `Down Payment Assistance`,
          to: downPaymentAssistanceLink,
        },
        {
          description: <p>Guidance made for life after closing.</p>,
          heading: `Existing Client Resources`,
          links: [
            {
              description: <p>Protect your home and what's inside.</p>,
              heading: `AB Insurance`,
              href: `https://abinsures.com/`,
            },
            // {
            //   description: <p>Your whole home loan journey in one spot.</p>,
            //   heading: `Home Warranty`,
            //   to: ``,
            // },
          ],
          to: homeownerHubLink,
        },
        {
          description: <p>Answers to your mortgage questions.</p>,
          heading: `Knowledge Center`,
          to: `/knowledge-center/`,
        },
        {
          description: <p>Plug in your numbers, see your options.</p>,
          heading: `Calculators`,
          links: [
            {
              description: <p>Find out how much home you can afford.</p>,
              heading: `Affordability Calculator`,
              to: `/affordability-calculator/`,
            },
          ],
          to: ``,
        },
      ],
    },
  }

  const corporateBaseCta = {
    text: `See What You Can Afford`,
    to: getUrlWithParams({
      path: `/prequalify-welcome/`,
      location: typeof window !== "undefined" ? window.location : undefined,
    }),
    ...props.base?.cta,
  }

  const corporateMobileBuyAHomeLinks = corporateLinks.buyAHome.links.map(
    link => {
      if (link.to === fthLink) {
        return {
          ...link,
          links: [
            {
              description: <p>Everything you need for your first home.</p>,
              heading: `First-Time Homebuyer Hub`,
              to: fthLink,
            },
            ...link.links,
          ],
        }
      }
      if (link.to === homeLoansLink) {
        return {
          ...link,
          links: [
            {
              description: <p>Get started here to find the right home loan.</p>,
              heading: `Your Home Loan Guide`,
              to: homeLoansLink,
            },
            ...link.links,
          ],
        }
      }
      return link
    },
  )

  const corporateNav: Props = {
    ...props,
    base: {
      cta: corporateBaseCta,
      links: [
        {
          dropdown: {
            ctaBanner: {
              cta: {
                text: `Find Your Local Expert`,
                to: `/search-mortgage-bankers/`,
              },
              description: (
                <p>Talk to someone who knows your specific market.</p>
              ),
            },
            description: corporateLinks.buyAHome.description,
            featuredProgram: {
              altText: `a family and dog sitting on floor at home`,
              description: (
                <p>
                  Your first home is closer than you think.  We're here to show
                  you exactly how to get there. 
                </p>
              ),
              heading: `First-Time Homebuyer`,
              image:
                data.FthFeaturedProgramImage.childImageSharp.gatsbyImageData,
              popularPaths: [
                {
                  text: `FHA Low Down Payments`,
                  to: `/knowledge-center/need-to-know-about-fha-loan/`,
                },
                {
                  text: `VA Military Benefits`,
                  to: `/knowledge-center/benefits-of-va-loans/`,
                },
              ],
              to: fthLink,
            },
            heading: corporateLinks.buyAHome.heading,
            links: corporateLinks.buyAHome.links,
          },
          text: corporateLinks.buyAHome.heading,
        },
        {
          dropdown: {
            ctaBanner: {
              cta: {
                text: `Find Your Local Expert`,
                to: `/search-mortgage-bankers/`,
              },
              description: <p>See if refinancing makes sense for you.</p>,
            },
            description: corporateLinks.refinance.description,
            featuredProgram: {
              altText: `a couple sitting on counter drinking coffee`,
              description: (
                <p>
                  Refinancing replaces your current mortgage with a new loan,
                  and Atlantic Bay Mortgage Group is here to help you find the
                  right option...
                </p>
              ),
              heading: `Refinance Your Mortgage on Your Terms`,
              image:
                data.RefinanceFeaturedProgramImage.childImageSharp
                  .gatsbyImageData,
              objectPosition: `50% 20%`,
              popularPaths: [
                {
                  text: `Pay Off Debt`,
                  to: `/knowledge-center/pay-debt-cash-out-refinance/`,
                },
                {
                  text: `Refinance a VA Loan`,
                  to: `/knowledge-center/how-to-refinance-a-va-loan-with-irrrl/`,
                },
              ],
              to: refinanceLink,
            },
            heading: corporateLinks.refinance.heading,
            links: corporateLinks.refinance.links,
          },
          text: corporateLinks.refinance.heading,
        },
        {
          dropdown: {
            ctaBanner: {
              cta: {
                text: `Find Your Local Expert`,
                to: `/search-mortgage-bankers/`,
              },
              description: <p>Get expert, local insight on your equity.</p>,
            },
            description: corporateLinks.homeEquity.description,
            featuredProgram: {
              altText: `couple standing using laptop together`,
              description: (
                <p>
                  Already know your home has value beyond the mortgage you owe?
                  This is where you find out what home equity is, how much...
                </p>
              ),
              heading: `Home Equity: Put What You've Built To Work`,
              image:
                data.HomeEquityFeaturedProgramImage.childImageSharp
                  .gatsbyImageData,
              objectPosition: `50% 20%`,
              popularPaths: [
                {
                  text: `Build Equity Faster`,
                  to: `/knowledge-center/build-your-homes-equity-faster-and-use-it-to-your-advantage/`,
                },
                {
                  text: `Reasons To Tap Into your Equity`,
                  to: `/knowledge-center/5-reasons-to-tap-into-your-home-equity/`,
                },
              ],
              to: homeEquityLink,
            },
            heading: corporateLinks.homeEquity.heading,
            links: corporateLinks.homeEquity.links,
          },
          text: corporateLinks.homeEquity.heading,
        },
        {
          dropdown: {
            ctaBanner: {
              cta: {
                text: `Find Your Local Expert`,
                to: `/search-mortgage-bankers/`,
              },
              description: <p>Talk rates with a local pro.</p>,
            },
            description: corporateLinks.rates.description,
            featuredProgram: {
              altText: `A woman sitting at a desk using her phone`,
              description: (
                <p>
                  A guide to how mortgage rates work for both purchases and
                  refinances, and why rates are only one piece...
                </p>
              ),
              heading: `Mortgage Rates: How They Work and What They Mean for You`,
              image:
                data.RatesFeaturedProgramImage.childImageSharp.gatsbyImageData,
              objectPosition: "50% 30%",
              popularPaths: [
                {
                  text: `A History Of Mortgage Rates`,
                  to: `/knowledge-center/history-of-mortgage-rates/`,
                },
                {
                  text: `Fixed Vs Adjustable Rates`,
                  to: `/knowledge-center/fixed-vs-adjustable-rate-mortgage/`,
                },
              ],
            },
            heading: corporateLinks.rates.heading,
            links: corporateLinks.rates.links,
            // to: mortgageRatesLink,
          },
          text: corporateLinks.rates.heading,
          to: mortgageRatesLink,
        },
        {
          dropdown: {
            ctaBanner: {
              cta: {
                text: `Find Your Local Expert`,
                to: `/search-mortgage-bankers/`,
              },
              description: <p>Get answers straight from a mortgage pro.</p>,
            },
            description: corporateLinks.resources.description,
            featuredProgram: {
              altText: `a couple wearing aprons cooking together`,
              description: (
                <p>
                  AB Insurance shops multiple, top-rated carriers to find
                  homeowner’s insurance coverage that fits your needs.
                </p>
              ),
              heading: `You work hard for your lifestyle, we work hard to protect it.`,
              image:
                data.ResourcesFeaturedProgramImage.childImageSharp
                  .gatsbyImageData,
              objectPosition: `50% 18%`,
              popularPaths: [
                {
                  text: `Homeowner Tax Benefits`,
                  to: `/knowledge-center/homeowner-tax-advantages-you-might-not-know/`,
                },
                {
                  text: `Annual Mortgage Review`,
                  to: `/knowledge-center/schedule-mortgage-review/`,
                },
              ],
              href: `https://abinsures.com/`,
            },
            heading: corporateLinks.resources.heading,
            links: corporateLinks.resources.links.map(link => {
              if (link.to === homeownerHubLink) {
                return {
                  ...link,
                  heading: `Homeowner Hub`,
                }
              }
              return link
            }),
          },
          text: corporateLinks.resources.heading,
        },
      ],
      socials,
    },
    mobile: {
      ctaLinks: [
        {
          text: `What You Can Afford?`,
          to: `/prequalify-welcome/`,
        },
        {
          text: `Find Your Local Expert`,
          to: `/search-mortgage-bankers/`,
        },
      ],
      links: [
        {
          dropdown: {
            ...corporateLinks.buyAHome,
            links: corporateMobileBuyAHomeLinks,
          },
          text: `Buy a Home`,
        },
        {
          dropdown: corporateLinks.refinance,
          text: `Refinance`,
        },
        {
          dropdown: corporateLinks.homeEquity,
          text: `Home Equity`,
        },
        {
          dropdown: corporateLinks.rates,
          text: `Rates`,
          to: mortgageRatesLink,
        },
        {
          dropdown: {
            ...corporateLinks.resources,
            links: corporateLinks.resources.links.map(link => {
              if (link.to === homeownerHubLink) {
                return {
                  ...link,
                  links: [
                    {
                      description: <p>A resource center for homeowners.</p>,
                      heading: `Homeowner Hub`,
                      to: homeownerHubLink,
                    },
                    ...link.links,
                  ],
                }
              }

              return link
            }),
          },
          text: `Resources`,
        },
        {
          dropdown: corporateLinks.about.links[0].dropdown,
          text: `About`,
        },
      ],
      socials,
    },
    upper: {
      links: [
        {
          dropdown: {
            links: [
              {
                text: `About Atlantic Bay`,
                to: `/about/`,
              },
              {
                text: `AB Cares Foundation`,
                to: `/abcares/`,
              },
              {
                text: `Careers`,
                to: `/careers/`,
                links: [
                  {
                    href: `https://www.joinatlanticbay.com`,
                    text: `Sales Careers`,
                  },
                ],
              },
              {
                text: `Contact`,
                to: `/contact/`,
              },
              {
                text: `Newsroom`,
                to: `/newsroom/`,
              },
            ],
          },
          text: "About",
        },
      ],
      socials,
    },
    variants: {
      useSearch: true,
      type: "corporate",
      ...props.variants,
    },
  }

  let mbSocials = socials.map(social => {
    if (isMounted && props.bankerData && props.bankerData[social.type])
      return {
        ...social,
        href: props.bankerData[social.type],
      }
    return null
  })

  let mbAboutLinks = [
    isMounted && props.bankerData?.calendly
      ? {
          description: <p>A quick conversation can get you mortgage ready.</p>,
          heading: `Schedule A Meeting`,
          text: `Schedule Meeting`,
          href: props.bankerData?.calendly,
        }
      : null,
    isMounted && props.bankerData?.reputationUrl
      ? {
          description: (
            <p>Read about my clients’ experiences working with me.</p>
          ),
          heading: `Reviews`,
          text: `Reviews`,
          to: `/${props.bankerData.slug}/reviews/`,
        }
      : null,
  ]

  let mbBaseLinks = corporateNav.base?.links?.map(link => {
    const ctaBanner = {
      ...link.dropdown?.ctaBanner,
      cta: {
        text: `Start Your Application`,
        href:
          isMounted && props.bankerData?.apply ? props.bankerData?.apply : "",
      },
    }

    if (link.text === "Resources") {
      return {
        ...link,
        dropdown: {
          ...link.dropdown,
          ctaBanner,
          links: [
            ...corporateLinks.resources.links
              .filter(link => link.heading !== "Calculators")
              .map(link => {
                if (link.to === homeownerHubLink) {
                  return {
                    ...link,
                    heading: `Homeowner Hub`,
                  }
                }
                return link
              }),
            {
              description: <p>Plug in your numbers, see your options.</p>,
              heading: `Calculator`,
              to:
                isMounted && props.bankerData
                  ? `/${props.bankerData.slug}/#calculator`
                  : ``,
            },
            {
              description: <p>Plug in your numbers, see your options.</p>,
              heading: `Make A Payment`,
              to: `/payments/`,
            },
            {
              description: (
                <p>A quick conversation to support your financial goals.</p>
              ),
              heading: `Annual Mortgage Review`,
              to:
                isMounted && props.bankerData
                  ? `/${props.bankerData.slug}/annual-mortgage-review/`
                  : ``,
            },
          ],
        },
      }
    }
    return {
      ...link,
      dropdown: link.dropdown
        ? {
            ...link.dropdown,
            ctaBanner,
          }
        : null,
    }
  })

  let mbMobileLinks = [
    ...corporateNav.mobile?.links?.map(link => {
      if (link.text === "Resources") {
        return {
          ...link,
          dropdown: {
            ...link.dropdown,
            links: [
              ...link.dropdown.links.filter(
                link => link.heading !== "Calculators",
              ),
              // .map(link => {
              //   if (link.to === homeownerHubLink) {
              //     return {
              //       ...link,
              //       heading: `Homeowner Hub`,
              //     }
              //   }
              //   return link
              // }),
              {
                description: <p>Plug in your numbers, see your options.</p>,
                heading: `Calculator`,
                to:
                  isMounted && props.bankerData
                    ? `/${props.bankerData.slug}/#calculator`
                    : ``,
              },
              {
                description: <p>Plug in your numbers, see your options.</p>,
                heading: `Make A Payment`,
                to: `/payments/`,
              },
              {
                description: (
                  <p>A quick conversation to support your financial goals.</p>
                ),
                heading: `Annual Mortgage Review`,
                to:
                  isMounted && props.bankerData
                    ? `/${props.bankerData.slug}/annual-mortgage-review/`
                    : ``,
              },
            ],
          },
        }
      }
      if (link.text === "About") {
        return {
          dropdown: {
            heading: "About",
            links: [
              ...corporateNav.mobile.links
                .find(link => link.text.match(/about/i))
                ?.dropdown?.links.filter(sublink => {
                  if (
                    (sublink as InternalLinkProps).to?.match(/careers/i) ||
                    (sublink as InternalLinkProps).to?.match(/newsroom/i)
                  ) {
                    return false
                  }
                  return true
                })
                .map(sublink => {
                  if (sublink.to?.match("contact")) {
                    return {
                      ...sublink,
                      to:
                        isMounted && props.bankerData?.slug
                          ? `/${props.bankerData?.slug}/contact/`
                          : "contact",
                    }
                  }
                  return sublink
                }),
              ...mbAboutLinks,
            ],
          },

          text: `About`,
        }
      }
      return {
        ...link,
      }
    }),
  ]

  let mbNav: Props = {
    ...corporateNav,
    base: {
      ...corporateNav.base,
      cta: {
        text: `Apply Now`,
        href:
          isMounted && props.bankerData?.apply ? props.bankerData?.apply : "",
        ...props.base?.cta,
      },
      links: mbBaseLinks,
    },
    mobile: {
      // ...corporateNav.mobile,

      ctaLinks: [
        {
          text: `Start Your Application`,
          href:
            isMounted && props.bankerData?.apply ? props.bankerData?.apply : "",
        },
      ],
      links: mbMobileLinks,
      // [
      //   ...mbBaseLinks.map(link => {
      //     if (link.text === "Buy a Home") {
      //       return {
      //         ...link,
      //         dropdown: {
      //           ...link.dropdown,
      //           links: corporateMobileBuyAHomeLinks,
      //         },
      //       }
      //     }
      //     return link
      //   }),
      //   {
      //     dropdown: {
      //       heading: "About",
      //       links: [
      //         ...corporateNav.mobile.links
      //           .find(link => link.text.match(/about/i))
      //           ?.dropdown?.links.filter(sublink => {
      //             if (
      //               (sublink as InternalLinkProps).to?.match(/careers/i) ||
      //               (sublink as InternalLinkProps).to?.match(/newsroom/i)
      //             ) {
      //               return false
      //             }
      //             return true
      //           })
      //           .map(sublink => {
      //             if (sublink.to?.match("contact")) {
      //               return {
      //                 ...sublink,
      //                 to:
      //                   isMounted && props.bankerData?.slug
      //                     ? `/${props.bankerData?.slug}/contact/`
      //                     : "contact",
      //               }
      //             }
      //             return sublink
      //           }),
      //         ...mbAboutLinks,
      //       ],
      //     },

      //     text: `About`,
      //   },
      // ],

      socials: mbSocials,
    },
    upper: {
      ...corporateNav.upper,
      links: corporateNav.upper.links.map(link => {
        if (link.text.match(/about/i)) {
          return {
            ...link,

            dropdown: {
              links: [
                ...link.dropdown.links
                  .filter(sublink => {
                    if (
                      (sublink as InternalLinkProps).to?.match(/careers/i) ||
                      (sublink as InternalLinkProps).to?.match(/newsroom/i)
                    ) {
                      return false
                    }
                    return true
                  })
                  .map(sublink => {
                    if (sublink.to?.match("contact")) {
                      return {
                        ...sublink,
                        to:
                          isMounted && props.bankerData?.slug
                            ? `/${props.bankerData?.slug}/contact/`
                            : "",
                      }
                    }
                    return sublink
                  }),
                ...mbAboutLinks,
              ],
            },
          }
        }
        return link
      }),
      socials: mbSocials,
    },
  }

  let otherNav: Props = {
    ...props,
    base: {
      ...props.base,
      cta: props.base?.cta
        ? {
            ...corporateBaseCta,
            ...props.base.cta,
          }
        : null,
    },
    mobile: {
      ...props.mobile,
      ctaLinks: props.mobile?.ctaLinks?.map(ctaLink => {
        return {
          ...corporateBaseCta,
          ...ctaLink,
        }
      }),
    },
  }
  const isMBPage =
    props.variants.type === "mb" || props.variants.type === "mb-other"
  let nav =
    props.variants.type === "corporate"
      ? corporateNav
      : props.variants.type === "mb"
        ? mbNav
        : props.variants.type === "other"
          ? otherNav
          : {
              ...props,
              base: props.base
                ? {
                    cta: {
                      text:
                        isMBPage && isMounted
                          ? `Apply Now`
                          : `See What You Can Afford`,
                      href:
                        props.bankerData && isMounted
                          ? props.bankerData.apply
                          : undefined,
                      ...props.base.cta,
                    },
                  }
                : null,
              mobile: props.mobile
                ? {
                    ...props.mobile,
                    ctaLinks: props.mobile?.ctaLinks?.map(ctaLink => {
                      return {
                        text:
                          isMBPage && isMounted
                            ? `Start Your Application`
                            : `See What You Can Afford`,
                        href:
                          props.bankerData && isMounted
                            ? props.bankerData.apply
                            : undefined,
                        ...ctaLink,
                      }
                    }),
                  }
                : null,
            }

  const [basicDropdown, setBasicDropdown] = React.useState(null)
  const [fullDropdown, setFullDropdown] = React.useState(null)
  const [sideDropdown, setSideDropdown] = React.useState(null)
  const [mobileDropdown, setMobileDropdown] = React.useState(null)
  const [inFullDropdown, setInFullDropdown] = React.useState(false)
  const [mobileScreens, setMobileScreens] = React.useState([])

  React.useEffect(() => {
    if (isMounted) {
      setMobileScreens(nav.mobile ? [nav.mobile] : [])
    }
  }, [isMounted, props.bankerData, props.pathname])

  const currentMobileScreen = mobileScreens[mobileScreens.length - 1]
  const inMobileDropdown = mobileScreens.length > 1
  const inLastMobileDropdown = mobileScreens.length > 2

  const logo = props.logo ? (
    props.logo
  ) : nav.variants.type !== "other" && nav.variants.type !== "mb-other" ? (
    <Link
      className={`${isMounted ? "opacity-100" : "opacity-0"} transition-all`}
      to={isMBPage && isMounted ? `/${props.bankerData?.slug}/` : `/`}
    >
      {isMBPage && isMounted && props.bankerData?.headshot ? (
        // props.pathname === `/${props.bankerData.slug}/` ? (
        //   <Logo />
        // ) : (
        <NavHeadshot
          logo={<Logo />}
          professional={{
            ...props.bankerData,
          }}
        />
      ) : (
        // )
        <Logo />
      )}
    </Link>
  ) : isMBPage && isMounted ? (
    <NavHeadshot
      logo={<Logo />}
      professional={{
        ...props.bankerData,
      }}
    />
  ) : (
    <Logo />
  )
  const baseLinksRef = React.useRef([])
  const fullDropdownRef = React.useRef(null)
  const fullDropdownsRef = React.useRef([])
  const fullDropdownBodyRef = React.useRef(null)

  const CtaBannerLinkTag = fullDropdown?.ctaBanner?.cta?.to ? Link : "a"
  const FeaturedProgramLinkTag = fullDropdown?.featuredProgram?.to ? Link : "a"
  const BaseCtaLinkTag = nav.base?.cta?.href ? "a" : Link
  const MobileCtaLinkTag =
    nav.mobile?.ctaLinks && nav.mobile?.ctaLinks[0]
      ? nav.mobile?.ctaLinks[0].to
        ? Link
        : "a"
      : "div"

  const [navIsPinned, setNavIsPinned] = React.useState(true)

  React.useEffect(() => {
    if (!fullDropdown) setSideDropdown(null)
  }, [fullDropdown])

  React.useEffect(() => {
    setFullDropdown(null)
    setSlideDirection(undefined)
  }, [props.pathname])

  React.useEffect(() => {
    if (!props.navIsOpen) {
      setMobileScreens([nav.mobile])
      setSlideDirection(undefined)
    }
  }, [props.navIsOpen])

  const [slideDirection, setSlideDirection] = React.useState(undefined)

  const custom = {
    slideDirection,
  }

  const screenVariants = {
    initial: custom => ({
      x: !custom.slideDirection
        ? 0
        : custom.slideDirection === "left"
          ? `100%`
          : `-100%`,
    }),
    animate: custom => ({
      x: 0,
    }),
    exit: custom => ({
      x: !custom.slideDirection
        ? undefined
        : custom.slideDirection === "left"
          ? `-100%`
          : `100%`,
    }),
  }

  return (
    <nav
      className="[--nav-logo-height:40px] bg-white relative z-[100] [&_a:hover]:no-underline [&_.logo-headshot]:[margin-inline-end:0]"
      onMouseLeave={() => {
        setFullDropdown(null)
      }}
    >
      <Headroom
        calcHeightOnResize
        disable={nav.variants.disableNavPinning}
        onUnpin={() => {
          setNavIsPinned(false)
          setFullDropdown(null)
        }}
        onPin={() => setNavIsPinned(true)}
      >
        {/* Desktop Container */}
        <div className="bg-white hidden [padding:22px_38px] relative min-nav-desktop:flex min-nav-desktop:flex-col min-nav-desktop:gap-[26px]">
          {/* Upper Container */}
          {nav.upper && (
            <div className="flex gap-[26px] items-center self-end w-fit min-w-[176px]">
              {nav.upper.links && (
                <ul>
                  {nav.upper.links.map(link => {
                    if (!link) return null
                    const typedInternalLink = link as InternalLinkProps
                    const typedExternalLink = link as ExternalLinkProps
                    const basicDropdownLink = (link as BasicDropdownLinkProps)
                      .dropdown
                      ? (link as BasicDropdownLinkProps)
                      : undefined
                    const typedRegularLink = typedInternalLink.to
                      ? typedInternalLink
                      : typedExternalLink

                    return (
                      <li
                        key={`basic-link-${link.text}`}
                        className="list_item--reset relative [&_>_*]:font-variant-numeric--secondary [&_>_*]:font-[500] [&_>_*]:leading-[20px] [&_>_*]:text-[14px] [&_>_*]:text-grey-7 [&_>_*]:tracking-[.56px]"
                        onKeyDown={e => {
                          if (e.key === "Escape") {
                            setBasicDropdown(null)
                          }
                        }}
                        onMouseMove={() => {
                          if (basicDropdownLink && !basicDropdown) {
                            setBasicDropdown(link)
                          }
                        }}
                        onMouseLeave={() => {
                          setBasicDropdown(null)
                        }}
                      >
                        {basicDropdownLink ? (
                          <button
                            className="flex gap-[4px]"
                            onFocus={() => {
                              setBasicDropdown(link)
                            }}
                            onBlur={() => {
                              setBasicDropdown(null)
                            }}
                          >
                            <span>{typedRegularLink.text}</span>
                            {/* {basicDropdown ? (
                              <ChevronUp className={`${chevronClasses}`} />
                            ) : (
                              <ChevronDown className={`${chevronClasses}`} />
                            )} */}
                            <ChevronDown
                              className={`${chevronClasses} ${
                                basicDropdown ? "rotate-[180deg]" : ""
                              }`}
                            />
                          </button>
                        ) : (
                          <Link
                            className="hover:text-pacific-blue"
                            to={(link as InternalLinkProps).to}
                          >
                            {typedRegularLink.text}
                          </Link>
                        )}
                        {basicDropdownLink && (
                          <div
                            className={`bg-white absolute [padding:24px_20px] [border-radius:0_0_16px_16px] [box-shadow:0_12px_32px_0_rgba(0,0,0,0.15)] w-max z-[20] ${
                              !basicDropdown ? "hidden" : ""
                            }`}
                          >
                            <ul className="flex flex-col gap-[20px]">
                              {basicDropdownLink.dropdown.links.map(
                                dropdownLink => {
                                  if (!dropdownLink) return null
                                  const typedInternalLink =
                                    dropdownLink as InternalLinkProps
                                  const typedExternalLink =
                                    dropdownLink as ExternalLinkProps
                                  const typedInternalParentLink =
                                    dropdownLink as InternalParentLinkProps
                                  const typedExternalParentLink =
                                    dropdownLink as ExternalParentLinkProps
                                  const parentLink = (
                                    dropdownLink as SharedParentLinkProps
                                  ).links
                                    ? (dropdownLink as SharedParentLinkProps)
                                    : undefined

                                  const typedRegularLink = typedInternalLink.to
                                    ? typedInternalLink
                                    : typedExternalLink

                                  let text = (typedRegularLink || parentLink)
                                    .text
                                  let to = typedInternalLink.to

                                  let href = (
                                    typedExternalLink || typedExternalParentLink
                                  ).href

                                  const LinkTag = to ? Link : "a"

                                  return (
                                    <li
                                      key={`upper-dropdown-link-${dropdownLink.text}`}
                                      className="group/parent-link list_item--reset flex flex-col gap-[20px] [&_>_*]:font-bold [&_>_*]:leading-[normal] [&_>_*]:text-[15px] [&_>_*]:text-grey-7"
                                    >
                                      <LinkTag
                                        className={`hover:text-pacific-blue`}
                                        to={to}
                                        href={href}
                                        target={href ? "_blank" : undefined}
                                      >
                                        {text}
                                      </LinkTag>
                                      {parentLink && (
                                        <ul className="flex flex-col gap-[20px]">
                                          {parentLink.links.map(sublink => {
                                            const typedInternalLink =
                                              sublink as InternalLinkProps
                                            const typedExternalLink =
                                              sublink as ExternalLinkProps
                                            const LinkTag = typedInternalLink.to
                                              ? Link
                                              : "a"
                                            return (
                                              <li
                                                key={`upper-link-sublink${sublink.text}`}
                                                className={twMerge(
                                                  `list_item--reset [&_*]:leading-[normal] ![padding-inline-start:32px] [&_*]:text-[15px] [&_*]:text-grey-7 `,
                                                )}
                                              >
                                                <LinkTag
                                                  className="hover:text-pacific-blue"
                                                  to={typedInternalLink.to}
                                                  href={typedExternalLink.href}
                                                  target={
                                                    typedExternalLink.href
                                                      ? "_blank"
                                                      : undefined
                                                  }
                                                >
                                                  {sublink.text}
                                                </LinkTag>
                                              </li>
                                            )
                                          })}
                                        </ul>
                                      )}
                                    </li>
                                  )
                                },
                              )}
                            </ul>
                          </div>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
              {nav.upper.socials && <Socials socials={nav.upper.socials} />}
            </div>
          )}
          {/* Base Container */}
          <div
            className="flex gap-[10px] items-center [&_svg]:max-h-[var(--nav-logo-height)] [&_svg]:max-w-[158px] [&_svg]:w-fit"
            onMouseMove={e => {
              const currentBaseLinkIndex = []
              const currentlyOnBaseLinkDropdownLink = baseLinksRef.current.find(
                (baseLink, i) => {
                  if (baseLink.contains(e.target)) {
                    currentBaseLinkIndex.push(i)
                    return true
                  }
                },
              )
              const currentlyInFullDropdown =
                // fullDropdownRef.current.contains(e.target) ||
                fullDropdownsRef.current.find(
                  dropdown => dropdown?.contains(e.target),
                ) || currentlyOnBaseLinkDropdownLink

              if (
                !currentlyInFullDropdown ||
                (currentlyOnBaseLinkDropdownLink &&
                  fullDropdown &&
                  currentBaseLinkIndex[0] &&
                  nav.base.links[currentBaseLinkIndex[0]]?.dropdown?.heading !==
                    fullDropdown.heading)
              ) {
                if (fullDropdown) setFullDropdown(null)
              }
            }}
          >
            <div className="flex items-center gap-[35px] [margin-inline-end:auto]">
              {logo}
              {nav.base?.links && (
                <ul className="flex flex-grow items-center justify-between  max-w-[753px] min-nav-desktop:[margin-inline:0_auto]">
                  {nav.base.links.map((link, i) => {
                    if (!link) return null
                    const typedDropdownLink = link as DropdownLinkProps
                    const shouldShowDropdown =
                      fullDropdown?.heading ===
                      typedDropdownLink.dropdown?.heading
                    return (
                      <li
                        className={twMerge(
                          ` list_item--reset [&_>_*]:font-variant-numeric--secondary [&_>_*]:leading-[20px] relative after:!content-[""] after:!absolute after:!h-[23px] after:!w-full  [&_*:not(svg)]:transition-all [&_>_*]:text-[16px] [&_>_*]:text-grey-7 [&_>_*]:tracking-[.64px]`,
                        )}
                        key={`base-link-${link.text}`}
                        ref={el => (baseLinksRef.current[i] = el)}
                      >
                        {typedDropdownLink.dropdown ? (
                          <button
                            className={`flex gap-[4px] items-center [padding:8px_12px] ${
                              shouldShowDropdown
                                ? "bg-[#EBF8FF] [&_*]:font-bold [&_*]:text-pacific-blue"
                                : ""
                            } `}
                            onFocus={() => {
                              setFullDropdown(typedDropdownLink.dropdown)
                            }}
                            onBlur={() => {
                              setFullDropdown(null)
                            }}
                            onMouseMove={() => {
                              if (typedDropdownLink.dropdown) {
                                setFullDropdown(typedDropdownLink.dropdown)
                              }
                            }}
                          >
                            <span>{link.text}</span>
                            <ChevronDown
                              className={`${chevronClasses} ${
                                shouldShowDropdown
                                  ? "rotate-[180deg] stroke-pacific-blue"
                                  : ""
                              }`}
                            />
                          </button>
                        ) : (
                          <Link
                            className="[padding:8px_12px] hover:text-pacific-blue "
                            to={(link as InternalLinkProps).to}
                          >
                            {link.text}
                          </Link>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
            <div className="flex gap-[24px] items-center [margin-inline-start:auto] ">
              {/* {nav.variants?.useSearch && (
                <button>
                  <Search className="[--size:20px] h-[var(--size)] w-[var(--size)]" />
                </button>
              )} */}
              {nav.base?.cta && (
                <BaseCtaLinkTag
                  className="button button--primary [padding-inline:20px]"
                  to={nav.base.cta.to}
                  href={nav.base.cta.href}
                  target={nav.base.cta.href ? "_blank" : undefined}
                >
                  {nav.base.cta.text}
                </BaseCtaLinkTag>
              )}
            </div>
            {/* Dropdown Container */}
            {nav.base?.links?.map((link, i) => {
              const typedDropdownLink = link as DropdownLinkProps
              if (!typedDropdownLink.dropdown) return null
              return (
                <FullDropdown
                  key={`full-dropdown-link-${link.text}`}
                  link={link}
                  fullDropdown={fullDropdown}
                  sideDropdown={sideDropdown}
                  setSideDropdown={setSideDropdown}
                  setFullDropdown={setFullDropdown}
                  fullDropdownsRef={fullDropdownsRef}
                  index={i}
                />
              )
            })}
          </div>
        </div>
        {/* Toggle Container (Mobile) */}
        {!nav.navIsOpen && (
          <div className="bg-white [box-shadow:0_1px_24px_0_rgba(119,119,119,0.15)] flex items-center justify-between [padding:12px_24px] [&_svg]:max-h-[var(--nav-logo-height)] [&_svg]:max-w-[158px] [&_svg]:w-fit min-nav-desktop:hidden">
            {logo}
            {(nav.mobile?.links || nav.mobile?.ctaLinks) && (
              <div className="flex gap-[24px]">
                {(nav.mobile?.links || nav.mobile?.ctaLinks) && (
                  <button
                    className={`flex-shrink-0 ${
                      !nav.mobile.links ? "min-tablet2:hidden" : ""
                    }`}
                    onClick={() => nav.setNavOpen(!nav.navIsOpen)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="40"
                      height="40"
                      viewBox="0 0 40 40"
                      fill="none"
                    >
                      <rect x="8" y="16" width="24" height="2" fill="black" />
                      <rect x="8" y="22" width="24" height="2" fill="black" />
                    </svg>
                  </button>
                )}
                {nav.mobile.ctaLinks && (
                  <MobileCtaLinkTag
                    className="button button--primary hidden min-tablet2:flex"
                    to={nav.mobile.ctaLinks[0].to}
                    href={nav.mobile.ctaLinks[0].href}
                    target={nav.mobile.ctaLinks[0].href ? "_blank" : undefined}
                  >
                    {nav.mobile.ctaLinks[0].text}
                  </MobileCtaLinkTag>
                )}
              </div>
            )}
          </div>
        )}
        {nav.variants.useKcNav && (
          <KcNav {...props} navIsPinned={navIsPinned} />
        )}
        {nav.variants.useNewsroomNav && (
          <NewsNav {...props} navIsPinned={navIsPinned} />
        )}
      </Headroom>
      <AnimatePresence>
        {nav.navIsOpen && (nav.mobile.links || nav.mobile.ctaLinks) && (
          <motion.div
            className={`[--padding-inline:24px] fixed flex flex-col gap-[24px] h-[100vh] overflow-y-auto [padding-block:24px_40px] w-full z-[100] min-nav-desktop:hidden ${
              inMobileDropdown ? "bg-grey-1" : "bg-white "
            }`}
            initial={{
              // opacity: 0,
              y: "-100%",
              // height: 0,
            }}
            animate={{
              // opacity: 1,
              // height: "auto",

              y: 0,
            }}
            exit={{
              // opacity: 0,
              // height: 0,
              y: "-100%",
            }}
            transition={{
              duration: 0.2,
              ease: "linear",
            }}
          >
            <AnimatePresence custom={custom} mode="popLayout">
              <motion.div
                key={`screen-${
                  currentMobileScreen.heading || `currentMobileScreen-${1}`
                }`}
                className="flex flex-col gap-[24px] w-full"
                initial="initial"
                animate="animate"
                exit="exit"
                custom={custom}
                variants={screenVariants}
                transition={{
                  bounceDamping: 100,
                  duration: 0.5,
                }}
                style={{
                  opacity: isMounted ? 1 : 0,
                }}
              >
                <header className="flex items-center justify-between [padding-inline:var(--padding-inline)] [&_svg]:max-h-[var(--nav-logo-height)] [&_svg]:w-fit">
                  {inMobileDropdown && (
                    <button
                      onClick={() => {
                        setMobileScreens(prevMobileScreens => {
                          const newMobileScreens = [...prevMobileScreens]
                          newMobileScreens.pop()
                          return newMobileScreens
                        })
                        setSlideDirection("right")
                      }}
                    >
                      <ArrowLeft className={chevronClasses} strokeWidth={2} />
                    </button>
                  )}
                  {logo}
                  <button
                    onClick={() => {
                      nav.setNavOpen(!nav.navIsOpen)
                    }}
                  >
                    <X className={chevronClasses} strokeWidth={2} />
                  </button>
                </header>
                {/* {nav.variants?.useSearch && (
              <div className="[padding-inline:var(--padding-inline)]">
                <div className="border border-grey-4 flex gap-[8px] items-center [padding:8px_12px] rounded-[4px]">
                  <label className="sr-only" htmlFor="search">
                    Search
                  </label>
                  <Search className="stroke-pacific-blue" />
                  <input
                    className="bg-transparent font-variant-numeric--secondary leading-[normal] text-[16px] placeholder:text-grey-5"
                    id="search"
                    placeholder="Search or jump to..."
                  />
                </div>
              </div>
            )} */}

                <div
                // initial={{ opacity: 0 }}
                // animate={{ opacity: 1 }}
                // exit={{ opacity: 0 }}
                // transition={{
                //   duration: 0.2,
                // }}
                >
                  {inMobileDropdown && (
                    <>
                      <p className="font-bold leading-[24px] [margin-block-end:0] [padding:13px_var(--padding-inline)] text-[20px] text-grey-7">
                        {currentMobileScreen.heading}
                      </p>
                      <hr className="border-b-[#E2E8F0]" />
                    </>
                  )}

                  {currentMobileScreen.links && (
                    <ul className="[padding-inline:var(--padding-inline)]">
                      {currentMobileScreen.links.map(link => {
                        if (!link) return null

                        const internalLink = link as InternalLinkProps
                        const externalLink = link as ExternalLinkProps
                        const dropdownLink = link as MobileDropdownLinkProps
                        const LinkTag = internalLink.to ? Link : "a"
                        const typedParentSublink = link as ParentSublinkProps
                        const typedInternalSublink =
                          link as InternalSublinkProps
                        const typedExternalSublink =
                          link as ExternalSublinkProps
                        const typedDropdownLink = link as SharedSublinkProps

                        const ItemTag =
                          dropdownLink.dropdown || typedParentSublink.links
                            ? "button"
                            : typedInternalSublink.to
                              ? Link
                              : "a"

                        const sublink = dropdownLink.dropdown
                          ? dropdownLink
                          : typedParentSublink

                        const isButton =
                          dropdownLink.dropdown || typedParentSublink.links

                        return (
                          <li
                            className={`list_item--reset  text-grey-7  transition-all  [&_>_*]:w-full [&_a:active]:no-underline ${
                              !inMobileDropdown
                                ? "border-b border-b-[#E2E8F0]"
                                : ""
                            }`}
                            key={`mobile-link-${link.text || link.heading}`}
                          >
                            <ItemTag
                              className={`group/link flex gap-[12px] items-center  [&_*]:text-left    ${
                                inLastMobileDropdown ? "" : "justify-between"
                              }`}
                              to={internalLink.to}
                              href={externalLink.href}
                              target={externalLink ? "_blank" : undefined}
                              onClick={() => {
                                if (isButton) {
                                  setMobileScreens(prevMobileScreens => [
                                    ...prevMobileScreens,
                                    dropdownLink.dropdown || link,
                                  ])
                                  setSlideDirection("left")
                                }
                              }}
                            >
                              {typedDropdownLink.heading ? (
                                <>
                                  {inLastMobileDropdown && (
                                    <div className="[--size:8px] bg-[#E2E8F0] h-[var(--size)] rounded-full w-[var(--size)] group-active/link:bg-pacific-blue group-focus/link:bg-pacific-blue"></div>
                                  )}
                                  <div
                                    className={`flex flex-col  [padding:18px_8px] w-full ${
                                      !inLastMobileDropdown
                                        ? "group-active/link:bg-[#EBF8FF]"
                                        : ""
                                    }`}
                                  >
                                    <p className="font-variant-numeric--secondary capitalize font-bold leading-[20px] [margin-block-end:0] text-[15px] text-grey-7 tracking-[.6px] group-active/link:text-pacific-blue group-focus/link:text-pacific-blue">
                                      {typedDropdownLink.heading}
                                    </p>
                                    <div className="[&_*]:font-variant-numeric--secondary [&_*]:leading-[20px] [&_*]:text-[13px] [&_*]:text-grey-6 [&_*]:tracking-[.52px] [&_p:last-child]:[margin-block-end:0]">
                                      {typedDropdownLink.description}
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <span className="[padding:18px_8px] text-[16px] active/link:text-pacific-blue">
                                  {link.text}
                                </span>
                              )}
                              {isButton && (
                                <ChevronRight className={`${chevronClasses}`} />
                              )}
                            </ItemTag>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>

                {((!inMobileDropdown && nav.mobile.socials) ||
                  nav.mobile.ctaLinks) && (
                  <div className="flex flex-col gap-[24px] [margin-block-start:auto] [padding-inline:var(--padding-inline)]">
                    {nav.mobile.ctaLinks && (
                      <div className="flex flex-col gap-[10px] [&_a]:w-full">
                        {nav.mobile.ctaLinks.map((link, i) => {
                          const LinkTag = link.to ? Link : "a"
                          return (
                            <LinkTag
                              key={`mobile-cta-link-${i}`}
                              to={link.to}
                              href={link.href}
                              target={link.href ? "_blank" : undefined}
                              className={twMerge(
                                `button ${
                                  i === 0
                                    ? "button--primary"
                                    : "button--tertiary"
                                }`,
                              )}
                            >
                              {link.text}
                            </LinkTag>
                          )
                        })}
                      </div>
                    )}
                    {!inMobileDropdown && nav.mobile.socials && (
                      <Socials socials={nav.mobile.socials} />
                    )}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}
